import { createUuidFromString } from '@shared/uuid/uuid';

import { legacyTrackableEvents } from './legacy-trackable-events';
import { trackableEvents } from './trackable-events';

import type { User } from '@shared/data-contract/user';
import type { LanguageKey } from '@shared/internationalization';
import type { TrackingEvent, TrackingEventName } from '@shared/tracking/tracking';
import type mixpanel from 'mixpanel-browser';

export type MixpanelClient = typeof mixpanel;

export type InitializeMixpanelTrackingOptions = {
    readonly languageKey: LanguageKey;
    readonly mixpanelProjectId: string;
    readonly tenantId: string;
    readonly user: User;
};

export type MixpanelTrackingService = {
    readonly sendTrackingEvent: (event: TrackingEvent) => void;
};

const allTrackableEvents: readonly TrackingEventName[] = [...legacyTrackableEvents, ...trackableEvents];

export const initializeMixpanelTracking = (
    client: MixpanelClient,
    options: InitializeMixpanelTrackingOptions,
): MixpanelTrackingService => {
    const { languageKey, mixpanelProjectId, tenantId, user } = options;

    client.init(mixpanelProjectId, {
        api_host: 'https://privacy.fe.cobrainer.cloud/track',
        api_routes: {
            engage: 'engage/',
            groups: 'groups/',
            track: 'track/',
        },
    });

    client.register({ tenant: tenantId });

    const isRecruiter = user.roles?.includes('recruiter') ?? false;
    const isHiringManager = user.roles?.includes('hiring_manager') ?? false;
    const userGroup = isRecruiter || isHiringManager ? 'Managing' : 'Consuming';

    client.identify(createUuidFromString(user.id));

    client.people.set({
        app_language: languageKey,
        is_hiring_manager: isHiringManager,
        is_recruiter: isRecruiter,
        user_group: userGroup,
    });

    return {
        sendTrackingEvent(event: TrackingEvent) {
            const { name, payload } = event;

            if (!allTrackableEvents.includes(name)) {
                return;
            }

            if (name === 'page-view') {
                client.track_pageview(payload);

                return;
            }

            client.track(name, payload);
        },
    };
};
